import React, { memo, useState, useEffect } from 'react';
import { Card, CardActionArea, Box, Typography, useTheme, Stack, Avatar, CardContent } from '@mui/material';
import { FlexibleDataItem, ApiEndpoint } from '../../types';
import { getThumbnailIcon, getSafeThumbnailUrl } from '../../utilities/mediaUtils';
import { getDataItemName, getDataItemDescription } from '../../utilities/dataSourceAdapters';
import { FulcrumImage } from '../common/FulcrumImage';

interface DataItemCardProps {
  item: FlexibleDataItem;
  compact?: boolean;
  imageUrl?: string;
  onSelect: (item: FlexibleDataItem) => void;
  preview?: boolean;
  endpoint?: ApiEndpoint | null;
  borderColor?: string; // For endpoint-based coloring in related items mode
}

// Helper function to detect media type from item
const getMediaType = (item: FlexibleDataItem): string => {
  // Check for media_type property
  if ('media_type' in item && item.media_type) {
    return item.media_type.toLowerCase();
  }
  
  // Check for URL or original_file to determine type
  if ('url' in item && item.url) {
    const url = item.url.toLowerCase();
    if (url.endsWith('.pdf')) return 'pdf';
    if (url.endsWith('.mp4') || url.endsWith('.mov') || url.endsWith('.avi')) return 'video';
    if (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.gif')) return 'image';
  }
  
  // Check original file info if available
  if ('original_file' in item && item.original_file?.type) {
    const fileType = item.original_file.type.toLowerCase();
    if (fileType.includes('pdf')) return 'pdf';
    if (fileType.includes('video') || fileType.includes('mp4') || fileType.includes('mov')) return 'video';
    if (fileType.includes('image')) return 'image';
  }
  
  // Check remote details if available
  if ('remote_details' in item && item.remote_details?.mime) {
    const mimeType = item.remote_details.mime.toLowerCase();
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('video')) return 'video';
    if (mimeType.includes('image')) return 'image';
  }
  
  return 'unknown';
};

const DataItemCard = memo(({
  item,
  compact,
  imageUrl,
  onSelect,
  preview = false,
  endpoint,
  borderColor
}: DataItemCardProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const mediaType = getMediaType(item);
  const theme = useTheme();
  
  // Determine if we should show the fallback
  // Prioritize the imageUrl prop (from DataGrid), fall back to getSafeThumbnailUrl
  const safeImageUrl = imageUrl || getSafeThumbnailUrl(item);
  const showFallback = !safeImageUrl || imageError;

  // Handle image loading states for better UX in compact mode
  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Reset states when image URL changes
  React.useEffect(() => {
    if (safeImageUrl) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [safeImageUrl]);

  // Conditional Rendering based on compact prop
  if (compact) {
    // ----- COMPACT MODE (Identical look to RelatedItemCard) ----- 
    return (
      <Card
        onClick={() => onSelect(item)} 
        sx={{ // Modern styling to match MediaDrawer
          width: 100, // Fixed width like RelatedItemCard
          height: 'auto', // Let content determine height
          cursor: "pointer",
          pointerEvents: "auto",
          backgroundColor: "rgba(26, 26, 26, 0.9)", // Modern dark background
          color: "white",
          backdropFilter: "blur(8px)", // Modern blur effect
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)", // Modern shadow
          border: `1px solid ${borderColor || "black"}`, // Use endpoint color or default black
          borderRadius: 2, // Modern rounded corners
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          transition: "all 0.3s ease-out", // Smooth transitions
          "&:hover": {
            border: "1px solid white", // Thinner white border on hover
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)", // Enhanced shadow on hover
            transform: "translateY(-2px)", // Subtle lift on hover like MediaDrawer
            zIndex: 10, 
          },
          // Performance optimizations
          willChange: "transform",
          backfaceVisibility: "hidden",
        }}
      >
        <CardContent
          sx={{ 
            pt: 1, px: 1, 
            pb: '2px', // Padding from RelatedItemCard
            width: "100%",
            "&:last-child": { pb: '2px' } 
          }}
        >
          <Stack spacing={0.5} alignItems="center"> {/* Spacing from RelatedItemCard */} 
            {/* Category Typography removed - not available in DataItemCard */}
 
            {/* Thumbnail Avatar or Enhanced Content */}
            {safeImageUrl && !showFallback ? (
              <Box sx={{ position: 'relative', width: 90, height: 90 }}>
                <FulcrumImage
                  src={safeImageUrl}
                  alt={`${item.name} thumbnail`}
                  region="AU"
                  thumbnail={true}
                  sx={{ 
                    width: 90, 
                    height: 90,
                    borderRadius: 1, // Match Avatar square variant
                    opacity: imageLoading ? 0.5 : 1,
                    transition: 'opacity 0.2s ease-in-out'
                  }}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
                {/* Show loading indicator while image loads */}
                {imageLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 90,
                      height: 90,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.6rem',
                      }}
                    >
                      Loading...
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              // Simple, clean fallback for compact mode
              <Box
                sx={{
                  width: '100%',
                  minHeight: 90,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  bgcolor: (theme) => theme.palette.grey[800],
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center',
                    fontSize: '0.7rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {getDataItemDescription(item, endpoint || undefined) || 
                   getDataItemName(item, endpoint || undefined) ||
                   `${endpoint?.name || 'Data'} item without image`}
                </Typography>
              </Box>
            )}
 
            {/* Entity Name */}
            <Typography
              variant="body2" // Variant from RelatedItemCard
              sx={{ 
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                fontSize: "0.7rem", // Font size from RelatedItemCard
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0, // From RelatedItemCard
                width: '100%'
              }}
            >
              {getDataItemName(item, endpoint || undefined)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  } else {
    // ----- NORMAL MODE (Subtle MediaDrawer-inspired styling) ----- 
    return (
      <CardActionArea
        style={{ 
          position: "relative",
          height: "100%",
          display: "block",
          cursor: preview ? 'default' : 'pointer',
          borderRadius: '8px', // Slightly more rounded like MediaDrawer
          overflow: 'hidden'
        }}
        onClick={() => {
          if (!preview) onSelect(item);
        }}
      >
        <Card
          sx={{
            height: "100%",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)", // Slightly more opaque than before
            backdropFilter: "blur(4px)", // Subtle blur effect
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)", // Enhanced shadow
            border: `1px solid ${borderColor || "rgba(255, 255, 255, 0.1)"}`, // Use endpoint color or default subtle border
            borderRadius: 2, // Modern rounded corners
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease-out", // Smooth transitions
            "&:hover": {
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
              transform: "translateY(-1px)", // Subtle lift
            },
          }}
        >
          {showFallback ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: 0,
                left: 0,
                backgroundColor: "rgba(30, 30, 30, 0.95)",
                p: 3,
              }}
            >
              {/* Enhanced content layout for no-image cards */}
              <Box
                sx={{
                  textAlign: 'center',
                  maxWidth: '100%',
                  color: 'white',
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    color: 'white',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {getDataItemName(item, endpoint || undefined)}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 2,
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {getDataItemDescription(item, endpoint || undefined) || 
                   'No additional details available for this item.'}
                </Typography>
                
                <Box
                  sx={{
                    mt: 'auto',
                    pt: 2,
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.75rem',
                    }}
                  >
                    {endpoint?.name || 'Data Item'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <FulcrumImage
              src={safeImageUrl} // Use safeImageUrl (includes fallback logic)
              alt={getDataItemName(item, endpoint || undefined)}
              region="AU"
              thumbnail={false} // Full size image, not thumbnail
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          )}
          {/* Only show text overlay when there's an actual image */}
          {!showFallback && (
            <Box sx={{ 
              position: "absolute", 
              bottom: 0, 
              left: 0,
              right: 0,
              padding: 2,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              zIndex: 1
            }}>
              <Typography 
                variant="subtitle1" 
                color="white"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: preview ? 'normal' : 'nowrap'
                }}
              >
                {getDataItemDescription(item, endpoint || undefined)}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              margin: 0,
              padding: "8px 16px",
              backgroundColor: "rgba(0, 0, 0, 0.8)", // Slightly transparent like MediaDrawer text area
              borderTop: "1px solid rgba(255, 255, 255, 0.1)", // Subtle separator like MediaDrawer
              zIndex: 1
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: "white",
                fontSize: "0.9rem",
                fontWeight: 500, // Match MediaDrawer font weight
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {getDataItemName(item, endpoint || undefined)}
            </Typography>
          </Box>
        </Card>
      </CardActionArea>
    );
  }
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return prevProps.imageUrl === nextProps.imageUrl && 
         prevProps.item.id === nextProps.item.id &&
         prevProps.preview === nextProps.preview;
});

export default DataItemCard;
